import { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Search } from "lucide-react";
import { Input } from "./ui/input";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import type { buyerFormData } from "@/schema/schema";
import { BuyersType } from "@/types/buyer";

export const SearchBuyerModal = ({
  isOpen,
  onClose,
  onSelectBuyer,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectBuyer: (buyer: buyerFormData) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: buyers,
    isLoading,
    error,
  } = useQuery({
      queryKey: ["buyers", searchTerm],
      queryFn: async () => {
        const response = await api.get<BuyersType>("/buyers", {
          params: {
            search: searchTerm,
          }
        })
        return response.data;
      },
      select: (data) =>
        data.data.filter(
          (item) =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase())
        ),
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Search Buyer</DialogTitle>
          <DialogDescription>
            Search for buyer's with name or tin.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by name or tin..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8"
              aria-label="Search items"
            />
          </div>
          <div className="relative border rounded-md">
            <Table>
              <TableHeader className="bg-white">
                <TableRow>
                  <TableHead className="w-[150px]">Code</TableHead>
                  <TableHead className="w-[150px]">Name</TableHead>
                  <TableHead className="w-[150px]">Registration Type</TableHead>
                  <TableHead className="w-[200px]">Registration No.</TableHead>
                  <TableHead className="w-[200px]">Email</TableHead>
                  <TableHead className="w-[150px]">Contact</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
            </Table>
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center text-red-500"
                      >
                        Error loading items
                      </TableCell>
                    </TableRow>
                  ) : buyers?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        No items found
                      </TableCell>
                    </TableRow>
                  ) : (
                    buyers?.map((buyer: buyerFormData) => (
                      <TableRow key={buyer.id} className="hover:bg-muted">
                        <TableCell className="w-[150px] max-w-[150px] truncate">
                          {buyer.code}
                        </TableCell>
                        <TableCell className="w-[150px] max-w-[150px] truncate">
                          {buyer.name}
                        </TableCell>
                        <TableCell className="w-[150px] max-w-[150px] truncate">
                          {buyer.registrationType}
                        </TableCell>
                        <TableCell className="w-[200px] max-w-[200px] truncate">
                          {buyer.registrationNumber}
                        </TableCell>
                        <TableCell className="w-[200px] max-w-[200px] truncate">
                          {buyer.email}
                        </TableCell>
                        <TableCell className="w-[150px] max-w-[150px] truncate">
                          {buyer.contactNumber}
                        </TableCell>
                        <TableCell className="w-[100px]">
                          <Button
                            size="sm"
                            onClick={() => {
                              onSelectBuyer({
                                code: buyer.code,
                                id: buyer.id,
                                name: buyer.name,
                                tin: buyer.tin,
                                registrationNumber: buyer.registrationNumber,
                                sstRegistrationNumber: buyer.sstRegistrationNumber,
                                email: buyer.email,
                                contactNumber: buyer.contactNumber,
                                addressId: buyer.addressId,
                                registrationType: buyer.registrationType,
                                buyerAddress: {
                                  id: buyer.buyerAddress.id,
                                  addressLine0: buyer.buyerAddress.addressLine0,
                                  addressLine1: buyer.buyerAddress.addressLine1,
                                  addressLine2: buyer.buyerAddress.addressLine2,
                                  postalZone: buyer.buyerAddress.postalZone,
                                  cityName: buyer.buyerAddress.cityName,
                                  state: buyer.buyerAddress.state,
                                  country: buyer.buyerAddress.country,
                                }
                              });
                              onClose();
                            }}
                            aria-label={`Select ${buyer.name}`}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchBuyerModal;
